import { useLayoutEffect, useRef, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";

const chipClass =
  "flex flex-col justify-center items-center px-2 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[#5F6477] font-OpenSans text-[12px] font-semibold leading-[18px] tracking-[0.12px] capitalize whitespace-nowrap";

function BranchDropDown({ user, branches, onChange }) {
  const [isDropDownShown, setIsDropDownShown] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);

  const containerRef = useRef(null);
  const measureRef = useRef(null);

  const selectedBranches = user.branches ?? [];
  const selectedIds = selectedBranches.map((b) => b.id);

  // Measure how many chips fit on a single line of the trigger, reserving room
  // for a "+N" overflow badge when not everything fits.
  useLayoutEffect(() => {
    const compute = () => {
      const container = containerRef.current;
      const measure = measureRef.current;
      if (!container || !measure) return;

      const gap = 4; // matches gap-1
      const available = container.clientWidth - 16; // px-2 on both sides
      const chips = Array.from(measure.children);
      const overflowWidth = chips.length ? chips[chips.length - 1].offsetWidth : 0;

      let used = 0;
      let count = 0;
      for (let i = 0; i < selectedBranches.length; i++) {
        const w = chips[i] ? chips[i].offsetWidth : 0;
        const needed = used + (count > 0 ? gap : 0) + w;
        const reserve =
          i < selectedBranches.length - 1 ? gap + overflowWidth : 0; // leave room for +N if more remain
        if (needed + reserve <= available) {
          used = needed;
          count++;
        } else {
          break;
        }
      }
      setVisibleCount(Math.max(count, selectedBranches.length ? 1 : 0));
    };

    compute();
    const ro = new ResizeObserver(compute);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds.join(",")]);

  const hiddenCount = selectedBranches.length - visibleCount;

  return (
    <OutsideClickHandler onOutsideClick={() => setIsDropDownShown(false)}>
      <div className="relative">
        {/* Hidden mirror used only to measure chip widths */}
        <div
          ref={measureRef}
          aria-hidden
          className="absolute invisible flex items-center gap-1 pointer-events-none -z-10"
        >
          {selectedBranches.map((branch) => (
            <span key={branch.id} className={chipClass}>
              {branch.name}
            </span>
          ))}
          <span className={chipClass}>+99</span>
        </div>

        <div
          ref={containerRef}
          onClick={() => setIsDropDownShown((v) => !v)}
          className="flex items-center gap-1 px-2 py-2 min-h-[36px] rounded-[6px] border border-[#E5E8F3] cursor-pointer hover:border-[#0058E5] overflow-hidden"
        >
          {selectedBranches.length === 0 ? (
            <span className="font-OpenSansRegular text-[12px] font-[400] text-[#9AA0AE]">Select branch</span>
          ) : (
            <>
              {selectedBranches.slice(0, visibleCount).map((branch) => (
                <span key={branch.id} className={chipClass}>
                  {branch.name}
                </span>
              ))}
              {hiddenCount > 0 && <span className={chipClass}>+{hiddenCount}</span>}
            </>
          )}
        </div>

        {isDropDownShown && (
          <div className="absolute w-full max-h-[250px] overflow-y-auto px-1 py-1 border border-[#E5E8F3] top-[40px] left-0 z-20 bg-white rounded-[8px] shadow-md">
            {branches.map((branch) => (
              <div
                key={branch.id}
                onClick={() => onChange(user, branch, !selectedIds.includes(branch.id))}
                className="w-full flex items-center gap-2 px-2 py-[6px] rounded-[6px] cursor-pointer hover:bg-[#F8F9FE]"
              >
                <input type="checkbox" readOnly checked={selectedIds.includes(branch.id)} className="cursor-pointer" />
                <p className="font-OpenSansRegular text-[12px] text-[#1A2332] font-[400] capitalize">{branch.name}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
}

export default BranchDropDown;
